import { useEffect, useState } from "react";
import { getActivities } from "./activityAPI";

type ActivityListProps = {
  type: string;
  id?: string | number;
  reload: boolean;
};

type Activity = {
  time: string; // ISO string or timestamp for the activity time
  title: string; // Title of the activity
  description?: string; // Optional description of the activity
  type?: string;
  icon?: string; // Optional icon class for the activity
  iconClass?: string; // Optional icon class for the activity
  [key: string]: any; // Allow additional properties if needed
};

// Define the structure of activityByDate
type ActivityByDate = {
  [date: string]: Activity[]; // Keys are dates (strings), values are arrays of Activity objects
};

const ActivityList = ({ type, id, reload }: ActivityListProps) => {
  const [activityData, setActivityData] = useState<ActivityByDate>();
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const { data } = await getActivities(type, id);
        const sortedData = data.data.sort(
          (a: any, b: any) =>
            new Date(b.time).getTime() - new Date(a.time).getTime()
        );
        const activityByDate: ActivityByDate = sortedData.reduce(
          (acc: any, activity: any) => {
            const date = new Intl.DateTimeFormat("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }).format(new Date(activity.time));
            if (!acc[date]) {
              acc[date] = [];
            }
            const type = activity.type;
            let iconClass = "";
            let icon = "";
            if (type === "CALL") {
              icon = "ti ti-phone";
              iconClass = "bg-secondary-success";
            }
            if (type === "MAIL") {
              icon = "ti ti-mail-code";
              iconClass = "bg-pending";
            }
            if (type === "TASK") {
              icon = "ti ti-notes";
              iconClass = "bg-orange";
            }
            if (type === "MEETING") {
              icon = "ti ti-user-pin";
              iconClass = "bg-info";
            }
            if (type === "EVENT") {
              icon = "ti ti-calendar-event";
              iconClass = "bg-success";
            }
            acc[date].push({ ...activity, iconClass, icon });
            return acc;
          },
          {}
        );
        setActivityData(activityByDate);
      } catch (error) {
        console.error("Error fetching activities:", error);
      }
    };

    fetchActivities();
  }, [id, reload, type]);

  return (
    <div className="tab-pane active show" id="activities">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h4 className="fw-semibold">Activities</h4>
        </div>
        <div className="card-body">
          {activityData && Object.keys(activityData).length > 0 ? (
            Object.keys(activityData).map((date: string) => (
              <div key={date}>
                <div className="badge badge-soft-purple fs-14 fw-normal shadow-none mb-3">
                  <i className="ti ti-calendar-check me-1" />
                  {date}
                </div>
                {activityData[date]?.map((activity: any, index: number) => (
                  <div key={index} className="card border shadow-none mb-3">
                    <div className="card-body p-3">
                      <div className="d-flex">
                        <span
                          className={`avatar avatar-md flex-shrink-0 rounded me-2 ${activity.iconClass}`}
                        >
                          <i className={activity.icon} />
                        </span>
                        <div>
                          <h6 className="fw-medium mb-1">{activity.title}</h6>
                          {activity.type === "TASK" && activity.description && (
                            <p className="mb-1">{activity.description}</p>
                          )}
                          <p>{new Date(activity.time).toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <p>No activities found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityList;
